import { Auth } from 'aws-amplify'
import { CognitoHostedUIIdentityProvider, CognitoUser } from '@aws-amplify/auth'
import { Logger } from '@meprism/app-utils'
import { MfaType } from '../redux/authentication/authenticationTypes'

export const AuthenticationService = {
  currentUser: async () => {
    return await Auth.currentUserPoolUser()
  },
  signInWithApple: async (destination = '/') => {
    try {
      Logger.debug('Attempting to Sign In With Apple')
      await Auth.federatedSignIn({
        provider: CognitoHostedUIIdentityProvider.Apple,
        customState: destination,
      })
    } catch (error) {
      Logger.error(`Sign In With Apple Failed ${error}`)
      throw error
    }
  },
  signInWithGoogle: async (destination = '/') => {
    try {
      Logger.debug('Attempting to Sign In With Google')
      await Auth.federatedSignIn({
        provider: CognitoHostedUIIdentityProvider.Google,
        customState: destination,
      })
    } catch (error) {
      Logger.error(`Sign In With Google Failed ${error}`)
      throw error
    }
  },
  signInWithFederation: async (
    destination = '/',
    setUser: any,
    applicationName: string,
  ) => {
    try {
      Logger.debug('Attempting to Sign In With Okta')
      return await Auth.federatedSignIn({
        customProvider: applicationName,
        customState: destination,
      })
    } catch (error) {
      Logger.error(`Sign In With okta Failed ${error}`)
      throw error
    }
  },
  signUpWithEmail: async (email: string, password: string) => {
    try {
      Logger.debug('Attempting to sign in with email')
      const confirmation = await Auth.signUp({
        username: email,
        password: password,
        autoSignIn: {
          enabled: true,
        },
      })
      Logger.debug('Login with email successful, awaiting confirmation')
      return confirmation
    } catch (error: any) {
      throw error.code
    }
  },
  confirmEmailSignup: async (userName: string, otp: string) => {
    Logger.debug(`Confirming user email signup for ${userName}`)
    try {
      await Auth.confirmSignUp(userName, otp)
    } catch (error: any) {
      Logger.error(`Error confirming user signup ${error}`)
      throw error.code
    }
  },
}

/**
 * helper to get the ID Token directly from storage without triggering 
 * Amplify's automatic background refresh logic.
 */
export const getValidLocalToken = (): string | null => {

  try {
    const config: any = Auth.configure();
    const clientId = config.userPoolWebClientId;
    if (!clientId) return null;

    const lastUser = localStorage.getItem(`CognitoIdentityServiceProvider.${clientId}.LastAuthUser`);
    if (!lastUser) return null;

    const idToken = localStorage.getItem(`CognitoIdentityServiceProvider.${clientId}.${lastUser}.idToken`);
    if (!idToken) return null;

    // decode JWT payload to check expiration
    const payload = JSON.parse(atob(idToken.split('.')[1]));
    const isExpired = Date.now() >= payload.exp * 1000;

    return isExpired ? null : idToken;
  } catch (error) {
    return null;
  }
};

export const getUserAuthToken = async (): Promise<string> => {
  const token = getValidLocalToken();
  if (token) {
    return token;
  }
  throw new Error('Session expired');
};

export const getMuidFromToken = async (): Promise<string | undefined> => {
  try {
    const token = await getUserAuthToken();
    const payload = JSON.parse(atob(token.split('.')[1]))
    return payload['custom:muid']
  } catch (error) {
    return undefined
  }
}

export const getCurrentAuthenticatedUser = async (): Promise<CognitoUser> => {
  await getUserAuthToken()
  return Auth.currentAuthenticatedUser()
}

export const updateUserAttributes = async (attributes: Record<string, any>) => {
  await getUserAuthToken()
  const user = await Auth.currentAuthenticatedUser()
  return await Auth.updateUserAttributes(user, attributes)
}

export const setPreferredMfa = async (method: MfaType) => {
  await getUserAuthToken()
  const user = await Auth.currentAuthenticatedUser()
  return await Auth.setPreferredMFA(user, method)
}

export const getUserMuid = async () => {
  try {
    const token = await getUserAuthToken();
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload['custom:muid'] || '';
  } catch (error) {
    console.error("No valid session found:", error);
    return '';
  }
}

export const getUserEmail = async () => {
  try {
    const user = await Auth.currentAuthenticatedUser();
    const payload = user?.getSignInUserSession()?.getIdToken()?.payload;

    const email = payload?.email || ''
    return String(email);
  } catch (error) {
    console.error('Error fetching user email:', error);
    return '';
  }
}

export const getUserCredentials = async () => {
  try {
    const muid = await getUserMuid();
    const email = await getUserEmail();
    return {
      email,
      muid
    }
  } catch (error) {
    console.error("Error fetching user credentials:", error);
    return {
      email: '',
      muid: ''
    }
  }
}

